import React, { createContext, useContext, useState, type ReactNode, use } from 'react';
import { AppDataContext } from '@/lib/app-data-hydration';

export interface VerificationIssue {
    type: 'error' | 'warning' | 'info';
    category: string;
    message: string;
    details?: any;
    documentData?: any;
}

export interface VerificationResult {
    success: boolean;
    timestamp: string;
    issues: VerificationIssue[];
    stats?: any;
}

interface AdminVerificationContextType {
    bibleResult: VerificationResult | null;
    setBibleResult: (result: VerificationResult | null) => void;
    contextResult: VerificationResult | null;
    setContextResult: (result: VerificationResult | null) => void;
    isVerifyingBible: boolean;
    setIsVerifyingBible: (val: boolean) => void;
    isVerifyingContext: boolean;
    setIsVerifyingContext: (val: boolean) => void;
    getIssuesForBook: (book: string) => VerificationIssue[];
}

const AdminVerificationContext = createContext<AdminVerificationContextType | undefined>(undefined);

const EMPTY_ARRAY: VerificationIssue[] = [];

export function AdminVerificationProvider({ children }: { children: ReactNode }) {
    const { data: appData } = use(AppDataContext);
    const [bibleResult, setBibleResult] = useState<VerificationResult | null>(null);
    const [contextResult, setContextResult] = useState<VerificationResult | null>(null);
    const [isVerifyingBible, setIsVerifyingBible] = useState(false);
    const [isVerifyingContext, setIsVerifyingContext] = useState(false);

    // Map for fast passage-to-book lookup
    const passageToBookMap = React.useMemo(() => {
        const map = new Map<string, string>();
        appData?.passages.forEach(p => map.set(p.id, p.book));
        return map;
    }, [appData?.passages]);

    // Pre-calculate the mapping of book names to issues
    const issuesByBook = React.useMemo(() => {
        const map = new Map<string, VerificationIssue[]>();

        const extractBook = (issue: VerificationIssue): string | undefined => {
            if (issue.details?.book) return issue.details.book;

            if (issue.details?.passageId) {
                const bookFromMap = passageToBookMap.get(issue.details.passageId);
                if (bookFromMap) return bookFromMap;
            }

            const bibleMatch = issue.message.match(/^(?:(?:[1-3]\s)?[A-Z][a-z]+)/);
            if (bibleMatch) return bibleMatch[0];

            const contextMatch = issue.message.match(/^([^/]+)\/([^/:]+)/);
            if (contextMatch) {
                const docId = contextMatch[2];
                if (contextMatch[1].toLowerCase().includes('passage')) {
                    const bookFromMap = passageToBookMap.get(docId);
                    if (bookFromMap) return bookFromMap;
                } else {
                    return docId;
                }
            }
            return undefined;
        };

        const addIssue = (issue: VerificationIssue) => {
            const bookName = extractBook(issue);
            if (bookName) {
                if (!map.has(bookName)) map.set(bookName, []);
                map.get(bookName)!.push(issue);
            }
        };

        bibleResult?.issues.forEach(addIssue);
        contextResult?.issues.forEach(addIssue);

        return map;
    }, [bibleResult, contextResult, passageToBookMap]);

    const getIssuesForBook = React.useCallback((book: string): VerificationIssue[] => {
        return issuesByBook.get(book) ?? EMPTY_ARRAY;
    }, [issuesByBook]);

    return (
        <AdminVerificationContext.Provider value={{
            bibleResult,
            setBibleResult,
            contextResult,
            setContextResult,
            isVerifyingBible,
            setIsVerifyingBible,
            isVerifyingContext,
            setIsVerifyingContext,
            getIssuesForBook
        }}>
            {children}
        </AdminVerificationContext.Provider>
    );
}

export function useAdminVerification() {
    const context = useContext(AdminVerificationContext);
    if (context === undefined) {
        throw new Error('useAdminVerification must be used within an AdminVerificationProvider');
    }
    return context;
}
